import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { Disc3, Mail, Lock, ShieldCheck } from "lucide-react";
import { getRememberedEmail, hasRememberedSession } from "@/lib/authStorage";

export default function Login() {
  const { login } = useAuth();
  const { t } = useI18n();
  const nav = useNavigate();
  const loc = useLocation();
  const params = new URLSearchParams(loc.search);
  const next = params.get("next") || null;
  const [email, setEmail] = useState(() => getRememberedEmail());
  const [password, setPassword] = useState("");
  const [rememberDevice, setRememberDevice] = useState(() => hasRememberedSession());
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await login(email.trim(), password, rememberDevice);
      toast.success(`Bun venit, ${u.business_name}`);
      nav(next || (["admin", "superadmin"].includes(u.role) ? "/admin" : "/dashboard"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("common.error"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell title={t("login.title")} subtitle={t("login.sub")}>
      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        <Field icon={Mail} type="email" placeholder={t("register.email")} value={email} setValue={setEmail} testid="login-email" />
        <Field icon={Lock} type="password" placeholder={t("register.password")} value={password} setValue={setPassword} testid="login-password" />
        <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-white/10 bg-black/20 px-4 py-3 text-sm text-zinc-300" data-testid="login-remember-device">
          <input
            type="checkbox"
            checked={rememberDevice}
            onChange={(e) => setRememberDevice(e.target.checked)}
            className="mt-1 h-4 w-4 accent-[#D4A23A]"
          />
          <span>
            <span className="flex items-center gap-2 font-medium text-white"><ShieldCheck size={15} /> Ține-mă minte pe acest dispozitiv</span>
            <span className="mt-1 block text-xs text-zinc-500">Sesiunea rămâne activă după închiderea browserului. Nu salva parola și folosește opțiunea doar pe dispozitivele tale.</span>
          </span>
        </label>
        <button
          type="submit" disabled={busy} data-testid="login-submit-btn"
          className="w-full rounded-full bg-[#D4A23A] py-3 font-semibold text-black transition hover:bg-[#E2B85C] disabled:opacity-60"
        >
          {busy ? t("common.loading") : t("login.submit")}
        </button>
      </form>
      <p className="mt-4 text-center text-sm">
        <Link to="/forgot-password" className="text-[#D4A23A] hover:underline">Ai uitat parola?</Link>
      </p>
      <p className="mt-6 text-center text-sm text-zinc-500">
        {t("login.noAccount")}{" "}
        <Link to="/register" className="text-[#D4A23A] hover:underline" data-testid="login-to-register">{t("nav.getStarted")}</Link>
      </p>
    </AuthShell>
  );
}

export function Field({ icon: Icon, type = "text", placeholder, value, setValue, testid, required = true }) {
  return (
    <div className="lime-focus flex items-center gap-3 rounded-xl border border-white/10 bg-[#121212] px-4 py-3">
      {Icon && <Icon size={16} className="text-zinc-500" />}
      <input
        data-testid={testid} type={type} required={required} placeholder={placeholder}
        value={value} onChange={(e) => setValue(e.target.value)}
        className="w-full bg-transparent text-sm text-white placeholder:text-zinc-500 focus:outline-none"
      />
    </div>
  );
}

export function AuthShell({ title, subtitle, children, wide = false }) {
  return (
    <div className={`mx-auto flex min-h-[calc(100vh-4rem)] ${wide ? "max-w-2xl" : "max-w-md"} flex-col justify-center px-4 py-12`}>
      <div className="mb-8 flex flex-col items-center gap-3 text-center">
        <span className="grid h-12 w-12 place-items-center rounded-xl bg-[#D4A23A] text-black">
          <Disc3 size={22} strokeWidth={2.5} />
        </span>
        <h1 className="font-display text-3xl font-black tracking-tight">{title}</h1>
        <p className="text-sm text-zinc-400">{subtitle}</p>
      </div>
      <div className="rounded-2xl border border-white/5 bg-[#121212] p-6">{children}</div>
    </div>
  );
}
